import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Building2, Package, ListChecks, ShoppingCart, Sparkles, Users, ArrowUpRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminOverview() {
  const ctx = useOutletContext();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Super-admin overview returns richer data; fall back to legacy if not super_admin.
    api.get("/super-admin/overview")
      .then((r) => setStats(r.data))
      .catch(() => api.get("/admin/overview").then((r) => setStats(r.data)).catch(() => {}));
  }, []);

  const isSuper = ctx?.user?.role === "super_admin";

  return (
    <div data-testid="admin-overview">
      <div className="overline">Console · Overview</div>
      <h1 className="mt-2 font-display text-3xl lg:text-5xl tracking-tighter">
        Hi {ctx?.user?.name || "Admin"}.
      </h1>
      <p className="mt-2 text-neutral-400">Here&apos;s what&apos;s happening across Fundle.</p>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Building2} label="Malls (live)" value={stats?.malls_live ?? stats?.malls} sub={stats?.malls_total ? `${stats.malls_total} total` : null} link="/admin/malls" />
        <Card icon={Package} label="Ad spaces" value={stats?.spaces_total ?? stats?.inventory} sub={stats?.spaces_available != null ? `${stats.spaces_available} available` : null} link="/admin/inventory" />
        <Card icon={Users} label="Advertisers" value={stats?.users_advertisers} link="/admin/users" />
        <Card icon={Users} label="Mall admins" value={stats?.users_mall_admins} sub={stats?.users_pending ? `${stats.users_pending} pending` : null} link="/admin/users" />
        <Card icon={ListChecks} label="Leads" value={stats?.leads_total ?? stats?.leads} sub={`${stats?.leads_new || 0} new`} link="/admin/leads" />
        <Card icon={ShoppingCart} label="Orders" value={stats?.orders_total ?? stats?.orders} sub={`${stats?.orders_active || stats?.orders_pending || 0} active`} link="/admin/orders" />
        <Card icon={Sparkles} label="AI plans" value={stats?.ai_plans} link="/admin/plans" />
        <Card icon={ShieldCheck} label="Pending approvals" value={stats?.users_pending} link="/admin/users" />
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="surface p-6">
          <div className="overline">Quick actions</div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {isSuper && <Link to="/admin/malls" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">Add a new mall <ArrowUpRight size={12} className="inline ml-1" /></Link>}
            {isSuper && <Link to="/admin/users" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">Create mall admin <ArrowUpRight size={12} className="inline ml-1" /></Link>}
            <Link to="/admin/leads" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">Review new leads <ArrowUpRight size={12} className="inline ml-1" /></Link>
            <Link to="/admin/orders" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">Open orders <ArrowUpRight size={12} className="inline ml-1" /></Link>
            <Link to="/admin/inventory" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">Edit inventory <ArrowUpRight size={12} className="inline ml-1" /></Link>
            <Link to="/admin/plans" className="bg-[#0a0a0a] border border-white/10 p-4 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-500">View AI plans <ArrowUpRight size={12} className="inline ml-1" /></Link>
          </div>
        </div>
        <div className="surface p-6">
          <div className="overline">System</div>
          <ul className="mt-4 space-y-2 text-sm text-neutral-300">
            <li><span className="text-neutral-500">Database:</span> MongoDB · seeded</li>
            <li><span className="text-neutral-500">AI:</span> Gemini Flash via Emergent Universal Key</li>
            <li><span className="text-neutral-500">Public site:</span> live at <Link to="/" className="text-amber-500">homepage</Link></li>
            <li><span className="text-neutral-500">Console mode:</span> {ctx?.user?.role}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, link }) {
  const inner = (
    <div className="surface p-5 hover:border-amber-500/40 transition-colors group">
      <div className="flex items-center justify-between">
        <Icon size={18} className="text-amber-500" />
        <ArrowUpRight size={14} className="text-neutral-600 group-hover:text-amber-500" />
      </div>
      <div className="mt-4 font-display text-3xl">{value ?? "—"}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500">
        {label}{sub ? ` · ${sub}` : ""}
      </div>
    </div>
  );
  return link ? <Link to={link} data-testid={`admin-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link> : inner;
}
